import { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import { useNotify } from 'react-admin';
import {
  addRenewalLines,
  coverageLabel,
  PROFILE_COVERAGE_FLAGS,
  type CoverageLine
} from '../renewals/renewalApi.ts';
import { getErrorMessage } from '../utils/helperUtils.ts';

const AddRenewalsDialog = ({
  lines,
  opportunityId,
  onClose
}: {
  lines: CoverageLine[];
  opportunityId: number;
  onClose: () => void;
}) => {
  const notify = useNotify();
  const [renewalDate, setRenewalDate] = useState('');
  const [carrier, setCarrier] = useState('');
  const [asPackage, setAsPackage] = useState(false);
  const [chosen, setChosen] = useState<CoverageLine[]>(lines);
  const [saving, setSaving] = useState(false);

  const coverages: CoverageLine[] = asPackage ? ['PACKAGE'] : chosen;

  const save = async () => {
    if (!renewalDate || !coverages.length) return;
    setSaving(true);
    try {
      const { created, skipped } = await addRenewalLines({
        opportunityId,
        coverages,
        renewalDate,
        incumbentCarrier: carrier.trim() || null
      });
      const added = created === 1 ? '1 renewal' : `${created} renewals`;
      notify(`${added} added${skipped ? `, ${skipped} already on file` : ''}`, { type: 'info' });
      onClose();
    } catch (err) {
      notify(getErrorMessage(err), { type: 'warning' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add renewals</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The profile asks for {lines.length === 1 ? 'one line' : `${lines.length} lines`}. Pick the
          date they renew and they go on the contact&apos;s renewals, linked to this deal.
        </Typography>
        <TextField
          fullWidth
          margin="normal"
          type="date"
          label="Renewal date"
          value={renewalDate}
          onChange={(event) => setRenewalDate(event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Incumbent carrier"
          value={carrier}
          onChange={(event) => setCarrier(event.target.value)}
        />
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Switch checked={asPackage} onChange={(event) => setAsPackage(event.target.checked)} />
          }
          label="They renew together as one package"
        />
        <FormGroup sx={{ mt: 0.5 }}>
          {lines.map((line) => (
            <FormControlLabel
              key={line}
              disabled={asPackage}
              control={
                <Checkbox
                  checked={asPackage || chosen.includes(line)}
                  onChange={(event) =>
                    setChosen((current) =>
                      event.target.checked
                        ? [...current, line]
                        : current.filter((value) => value !== line)
                    )
                  }
                />
              }
              label={coverageLabel(line)}
            />
          ))}
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || !renewalDate || !coverages.length}
        >
          {coverages.length === 1 ? 'Add renewal' : `Add ${coverages.length} renewals`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Offered when the profile has coverage lines ticked: most of the typing is
// already done, so one date makes them renewals.
export const AddRenewalsFromProfile = ({
  profile,
  opportunityId
}: {
  profile: Record<string, unknown>;
  opportunityId?: number;
}) => {
  const [open, setOpen] = useState(false);
  const lines = useMemo(
    () =>
      Object.entries(PROFILE_COVERAGE_FLAGS)
        .filter(([flag]) => profile[flag] === true)
        .map(([, line]) => line),
    [profile]
  );
  if (!lines.length || !opportunityId) return null;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<EventRepeatOutlinedIcon />}
        onClick={() => setOpen(true)}
      >
        Add renewals
      </Button>
      {open ? (
        <AddRenewalsDialog
          lines={lines}
          opportunityId={opportunityId}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
};
